// Login / Registration — v0.7.0 (Auth + Saved Analyses)
//
// One screen, three modes: sign in, create account, reset password.
// Email + password auth via Supabase (AuthContext). On success the user
// is sent back to wherever the protected-route guard bounced them from,
// defaulting to /dashboard.
//
// 2FA note: when TOTP / passkey enrollment lands in the next increment,
// the sign-in handler is where a "factor required" branch will go —
// signIn() will resolve, AuthContext.assuranceLevel will read 'aal1',
// and we'll route to a challenge step before navigating away.

import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { isOnboardingAllowed } from '../lib/onboardingAllowlist';

// HF-CO-001 — Controlled Onboarding Authentication Remediation. Shown when an
// email is not on the fail-closed controlled-onboarding allowlist (the same
// gate that governs Google OAuth at /auth/callback). Used to deny BOTH
// email/password account creation (pre-creation) and a non-cohort sign-in.
const INVITE_ONLY_MESSAGE =
  "This email isn't on the controlled-onboarding invite list. Rentava is " +
  'invite-only right now — use the email address on your invitation, or ' +
  'contact the Rentava team if you believe this is a mistake.';

// ---- shared styles (matches the v0.6.0 DealAnalyzer aesthetic) ----
const pageStyle = {
  background: '#f3f4f6',
  minHeight: '100vh',
  padding: '60px 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '36px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  width: '100%',
  maxWidth: '440px',
};

const inputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  border: '1px solid #d1d5db',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontWeight: 700,
  marginBottom: '8px',
  fontSize: '15px',
  color: '#111827',
};

const primaryButton = (enabled) => ({
  width: '100%',
  background: enabled ? '#1d4ed8' : '#9ca3af',
  color: '#ffffff',
  border: 'none',
  borderRadius: '12px',
  padding: '15px',
  fontSize: '17px',
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

const linkButton = {
  background: 'none',
  border: 'none',
  color: '#1d4ed8',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '14px',
  padding: 0,
};

// SSO / social login buttons. Plain bordered buttons so they read as
// "alternative to" the primary email/password action above them.
const providerButton = (enabled) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  background: '#ffffff',
  color: enabled ? '#111827' : '#9ca3af',
  border: '1px solid #d1d5db',
  borderRadius: '12px',
  padding: '13px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: enabled ? 'pointer' : 'not-allowed',
  marginBottom: '10px',
});

const dividerRow = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  margin: '22px 0 16px',
  color: '#9ca3af',
  fontSize: '13px',
};

const dividerLine = { flex: 1, height: '1px', background: '#e5e7eb' };

const noticeBox = (tone) => {
  const tones = {
    error: { border: '#ef4444', bg: '#fff5f5', color: '#b91c1c' },
    success: { border: '#22c55e', bg: '#f0fdf4', color: '#15803d' },
    info: { border: '#d1d5db', bg: '#f9fafb', color: '#374151' },
  };
  const t = tones[tone] || tones.info;
  return {
    marginBottom: '20px',
    padding: '14px',
    borderRadius: '12px',
    border: `1px solid ${t.border}`,
    background: t.bg,
    color: t.color,
    fontSize: '14px',
    lineHeight: 1.5,
  };
};

const MODES = { SIGN_IN: 'sign_in', SIGN_UP: 'sign_up', RESET: 'reset' };

// SSO providers. `id` is the Supabase provider key.
//
// Controlled Onboarding Release Readiness (2026-06-20): Google OAuth is
// RE-ENABLED for the invite-only controlled cohort. This does NOT reopen
// public self-serve signup — the SIGN_UP entry point stays removed and every
// OAuth session is cohort-gated at /auth/callback (see lib/onboardingAllowlist;
// fail-closed, admins always admitted). Apple and Microsoft stay dormant
// (Apple DEFERRED: architecturally a clean fit, but Apple Developer enrollment
// + production provider config + email-relay/account-linking are out of scope —
// re-enabling later is the same one-line flip). The wiring
// (handleOAuth / signInWithOAuth / AuthContext) is unchanged.
const PROVIDERS = [
  { id: 'google', label: 'Continue with Google', enabled: true },
  { id: 'apple', label: 'Continue with Apple', enabled: false },
  { id: 'azure', label: 'Continue with Microsoft', enabled: false },
];

// True only when at least one SSO provider is enabled. When all are disabled
// (current waitlist-only posture) the entire SSO block is hidden so no public
// OAuth onboarding affordance is rendered.
const ANY_PROVIDER_ENABLED = PROVIDERS.some((p) => p.enabled);

// Where the SSO callback page should send the user after sign-in. Stashed
// before the redirect because OAuth round-trips lose React Router state.
const POST_AUTH_REDIRECT_KEY = 'rv-str-postauth-redirect';

export default function Login() {
  const { signIn, signUp, resetPassword, signInWithOAuth, signOut, isConfigured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to send the user after a successful sign-in. The protected-route
  // guard stashes the attempted path in location.state.from; with no
  // attempted path, fresh sign-ins land on the Dashboard (consistent with
  // the SSO fallback in AuthCallback.js).
  const redirectTo = location.state?.from?.pathname || '/dashboard';

  const [mode, setMode] = useState(MODES.SIGN_IN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Holds the provider id that's mid-redirect (or '' when none).
  const [oauthBusy, setOauthBusy] = useState('');

  function switchMode(nextMode) {
    setMode(nextMode);
    setError('');
    setNotice('');
    setPassword('');
    setConfirmPassword('');
  }

  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const passwordValid = password.length >= 8;

  let canSubmit = isConfigured && !busy && emailValid;
  if (mode === MODES.SIGN_IN) canSubmit = canSubmit && password.length > 0;
  if (mode === MODES.SIGN_UP) {
    canSubmit = canSubmit && passwordValid && password === confirmPassword;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError('');
    setNotice('');

    try {
      if (mode === MODES.SIGN_IN) {
        const { error: signInError } = await signIn(email.trim(), password);
        if (signInError) {
          setError(signInError.message || 'Unable to sign in.');
          return;
        }

        // HF-CO-001 — Controlled-onboarding cohort parity. A successful
        // password sign-in for an email that isn't in the cohort is ended
        // immediately, mirroring the OAuth cohort gate at /auth/callback so
        // email/password is at least as restrictive as Google. Admins always
        // pass (the allowlist includes REACT_APP_ADMIN_EMAILS). Fail-closed:
        // an empty allowlist admits no one but admins.
        if (!isOnboardingAllowed(email.trim())) {
          if (typeof signOut === 'function') {
            await Promise.resolve(signOut()).catch(() => {});
          }
          setError(INVITE_ONLY_MESSAGE);
          return;
        }

        // Check if MFA is required.
        //
        // IMPORTANT: the `aal` (Authenticator Assurance Level) claim
        // lives inside the access_token JWT, not on session.user — so
        // `session.user.aal` is always undefined and can't be used as
        // the gate. The canonical Supabase helper is
        // `auth.mfa.getAuthenticatorAssuranceLevel()`, which returns
        // `{ currentLevel, nextLevel }` after decoding the JWT.
        //
        // After a password-only sign-in for a user who has a verified
        // factor enrolled, Supabase reports `currentLevel='aal1'` and
        // `nextLevel='aal2'`. That mismatch is the signal to step the
        // user through the TOTP challenge before letting them into
        // protected routes.
        const { data: aalData, error: aalError } =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (aalError) {
          // Don't block sign-in on an AAL lookup failure — log it and
          // fall through to the normal redirect. (If the user has MFA
          // enabled, protected routes can still gate later.)
          console.warn('AAL lookup failed:', aalError);
        } else if (
          aalData?.currentLevel &&
          aalData?.nextLevel &&
          aalData.currentLevel !== aalData.nextLevel
        ) {
          navigate('/auth/challenge', {
            replace: true,
            state: { from: { pathname: redirectTo } },
          });
          return;
        }

        // No MFA required or user is already aal2; proceed to the intended destination.
        navigate(redirectTo, { replace: true });
        return;
      }

      if (mode === MODES.SIGN_UP) {
        // HF-CO-001 — Controlled-onboarding cohort gate. Block account
        // creation BEFORE signUp() for any email not on the fail-closed
        // allowlist, so re-exposing the sign-up surface can never reopen
        // public registration (Public Access Lockdown preserved, ADR-028).
        // Same provider-agnostic allowlist as the Google OAuth path (ADR-049).
        if (!isOnboardingAllowed(email.trim())) {
          setError(INVITE_ONLY_MESSAGE);
          return;
        }

        const { data, error: signUpError } = await signUp(email.trim(), password);
        if (signUpError) {
          setError(signUpError.message || 'Unable to create account.');
          return;
        }
        // If the project requires email confirmation, there's no session yet.
        if (data?.session) {
          navigate(redirectTo, { replace: true });
        } else {
          // switchMode clears state, so set the notice *after* switching.
          switchMode(MODES.SIGN_IN);
          setNotice(
            `Account created. Check ${email.trim()} for a confirmation link, then sign in.`
          );
        }
        return;
      }

      if (mode === MODES.RESET) {
        const { error: resetError } = await resetPassword(email.trim());
        if (resetError) {
          setError(resetError.message || 'Unable to send reset email.');
          return;
        }
        setNotice(
          `If an account exists for ${email.trim()}, a password reset link is on its way.`
        );
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleOAuth(providerId) {
    setError('');
    setNotice('');
    setOauthBusy(providerId);

    // Preserve the intended destination across the OAuth round-trip — the
    // /auth/callback page reads this back once the session resolves.
    try {
      window.sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, redirectTo);
    } catch {
      // sessionStorage may be unavailable (private mode); callback falls
      // back to its default destination, which is fine.
    }

    const { error: oauthError } = await signInWithOAuth(providerId);
    if (oauthError) {
      setError(oauthError.message || 'Unable to start sign-in.');
      setOauthBusy('');
    }
    // On success the browser is already navigating to the provider —
    // there's nothing left to do in this component.
  }

  const heading =
    mode === MODES.SIGN_IN
      ? 'Sign in'
      : mode === MODES.SIGN_UP
      ? 'Create your account'
      : 'Reset your password';

  const subheading =
    mode === MODES.SIGN_IN
      ? 'Access your saved analyses and investor workspace.'
      : mode === MODES.SIGN_UP
      ? 'Save analyses, track deals, and build your investor workspace.'
      : "Enter your email and we'll send you a reset link.";

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>
          {heading}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '15px', margin: '0 0 24px', lineHeight: 1.5 }}>
          {subheading}
        </p>

        {!isConfigured && (
          <div style={noticeBox('info')}>
            <strong>Auth isn't configured yet.</strong> Add{' '}
            <code>REACT_APP_SUPABASE_URL</code> and <code>REACT_APP_SUPABASE_ANON_KEY</code> to{' '}
            <code>frontend/.env</code>, then restart the dev server.
          </div>
        )}

        {error && <div style={noticeBox('error')}>{error}</div>}
        {notice && <div style={noticeBox('success')}>{notice}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '18px' }}>
            <label htmlFor="email" style={labelStyle}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
              disabled={busy}
            />
          </div>

          {mode !== MODES.RESET && (
            <div style={{ marginBottom: '18px' }}>
              <label htmlFor="password" style={labelStyle}>
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete={mode === MODES.SIGN_UP ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === MODES.SIGN_UP ? 'At least 8 characters' : 'Your password'}
                style={inputStyle}
                disabled={busy}
              />
              {mode === MODES.SIGN_UP && password.length > 0 && !passwordValid && (
                <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>
                  Use at least 8 characters.
                </div>
              )}
            </div>
          )}

          {mode === MODES.SIGN_UP && (
            <div style={{ marginBottom: '18px' }}>
              <label htmlFor="confirmPassword" style={labelStyle}>
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                style={inputStyle}
                disabled={busy}
              />
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>
                  Passwords don't match.
                </div>
              )}
            </div>
          )}

          <button type="submit" disabled={!canSubmit} style={primaryButton(canSubmit)}>
            {busy
              ? 'Working…'
              : mode === MODES.SIGN_IN
              ? 'Sign in'
              : mode === MODES.SIGN_UP
              ? 'Create account'
              : 'Send reset link'}
          </button>
        </form>

        {/* SSO / social login — hidden in reset-password mode, and hidden
            entirely when no provider is enabled (ANY_PROVIDER_ENABLED is
            false). Only ENABLED providers are rendered: during controlled
            onboarding that is Google alone — deferred providers (Apple /
            Microsoft) are not advertised as "coming soon". */}
        {mode !== MODES.RESET && ANY_PROVIDER_ENABLED && (
          <>
            <div style={dividerRow}>
              <div style={dividerLine} />
              <span>or</span>
              <div style={dividerLine} />
            </div>

            {PROVIDERS.filter((provider) => provider.enabled).map((provider) => {
              const isBusy = oauthBusy === provider.id;
              const clickable =
                provider.enabled && isConfigured && !oauthBusy && !busy;
              return (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => handleOAuth(provider.id)}
                  disabled={!clickable}
                  style={providerButton(clickable)}
                  title={provider.enabled ? '' : 'Coming soon'}
                >
                  {isBusy ? 'Redirecting…' : provider.label}
                  {!provider.enabled && (
                    <span
                      style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}
                    >
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}

        {/* mode switching
            HF-CO-001 (Controlled Onboarding Authentication Remediation):
            invite-only email/password onboarding is enabled alongside Google
            OAuth. The sign-up affordance below is INVITE-FRAMED ("Have an
            invite?") and account creation is fail-closed — handleSubmit checks
            isOnboardingAllowed(email) BEFORE signUp(), so this does NOT reopen
            public self-serve registration (Public Access Lockdown preserved,
            ADR-028; same allowlist as the OAuth cohort gate, ADR-049). */}
        <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {mode === MODES.SIGN_IN && (
            <>
              <button
                type="button"
                style={linkButton}
                onClick={() => switchMode(MODES.RESET)}
              >
                Forgot your password?
              </button>
              <button
                type="button"
                style={linkButton}
                onClick={() => switchMode(MODES.SIGN_UP)}
              >
                Have an invite? Create your account
              </button>
            </>
          )}

          {(mode === MODES.RESET || mode === MODES.SIGN_UP) && (
            <button
              type="button"
              style={linkButton}
              onClick={() => switchMode(MODES.SIGN_IN)}
            >
              ← Back to sign in
            </button>
          )}
        </div>

        <div style={{ marginTop: '24px', fontSize: '13px', color: '#9ca3af', lineHeight: 1.5 }}>
          Two-factor authentication via authenticator app is available — set it
          up in Account → Security after you sign in. Passkey support is on the
          way. <Link to="/" style={{ color: '#6b7280' }}>Back to home</Link>
        </div>
      </div>
    </div>
  );
}
